import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, Link } from "react-router-dom";
import {
  AuthContainer,
  AuthSidebar,
  LogoContainer,
  LogoSeparator,
  BrandLogo,
  AnniversaryLogo,
  AuthTitle,
  AuthContent,
  PoolProLogo,
  SidebarFormContainer,
  FormGroup,
  Label,
  Input,
  PasswordField,
  PasswordInput,
  PasswordToggle,
  ErrorMessage,
  Button,
  FormSection,
  DividerLine,
  AuthLink,
  ForgotLinkRow,
  LinkRow,
  LoadingSpinner,
} from "styles/auth.styles";
import daveyLogo from "assets/Davey_logotype_Yellow.svg";
import anniversaryLogo from "assets/90_years_logo_White.svg";
import poolProfessionalLogo from "assets/PoolProfessional_STACK_Yellow_White.png";
import { useAuth } from "context/AuthContext";
import { notify } from "utils/notify";
import {
  executeRecaptchaEnterprise,
  isRecaptchaEnterpriseEnabled,
} from "utils/recaptchaEnterprise";

/** Site key from GitHub Environments → prod → REACT_APP_RECAPTCHA_SITE_KEY. */
const RECAPTCHA_SITE_KEY = (
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""
).trim();

/** Backend Postman/local bypass — used when Enterprise is not enabled. */
const RECAPTCHA_LOCAL_BYPASS = "LgServicePortal";

const useEnterpriseRecaptcha =
  isRecaptchaEnterpriseEnabled(RECAPTCHA_SITE_KEY);

type SignInFormData = {
  email: string;
  password: string;
};

const schema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters"),
});

const SignIn: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: SignInFormData) => {
    setIsSubmitting(true);
    try {
      let token = RECAPTCHA_LOCAL_BYPASS;
      if (useEnterpriseRecaptcha) {
        token = await executeRecaptchaEnterprise(
          RECAPTCHA_SITE_KEY,
          "LOGIN",
        );
      }

      const user = await login({
        email: data.email,
        password: data.password,
        recaptchaToken: token,
      });

      user?.user.role === "ADMIN"
        ? navigate("/all-devices")
        : navigate("/devices");
    } catch (error) {
      console.error("Login error:", error);
      if (useEnterpriseRecaptcha) {
        notify.error("Security check failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthContainer>
      <AuthSidebar>
        <LogoContainer>
          <BrandLogo src={daveyLogo} alt="Davey Logo" />
          <LogoSeparator />
          <AnniversaryLogo src={anniversaryLogo} alt="90 Years Logo" />
        </LogoContainer>

        <AuthTitle>PoolProfessional</AuthTitle>
        <SidebarFormContainer>
          <FormSection>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  autoComplete="email"
                  {...register("email")}
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <ErrorMessage>{errors.email.message}</ErrorMessage>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="password">Password</Label>
                <PasswordField>
                  <PasswordInput
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    {...register("password")}
                    disabled={isSubmitting}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    disabled={isSubmitting}
                  >
                    <i
                      className={`pi ${showPassword ? "pi-eye-slash" : "pi-eye"}`}
                    />
                  </PasswordToggle>
                </PasswordField>
                {errors.password && (
                  <ErrorMessage>{errors.password.message}</ErrorMessage>
                )}
              </FormGroup>

              <ForgotLinkRow>
                <AuthLink as={Link} to="/auth/forgot-password">
                  Forgot password?
                </AuthLink>
              </ForgotLinkRow>

              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <LoadingSpinner /> : "Sign In"}
              </Button>
            </form>
          </FormSection>

          <DividerLine />

          <LinkRow>
            <AuthLink as={Link} to="/terms-conditions">
              Terms & Conditions
            </AuthLink>
          </LinkRow>
        </SidebarFormContainer>
      </AuthSidebar>

      <AuthContent>
        <PoolProLogo
          src={poolProfessionalLogo}
          alt="Davey Pool Professional Logo"
        />
      </AuthContent>
    </AuthContainer>
  );
};

export default SignIn;
